<template>
  <div
    class="archive"
    :class="getClassArchive()"
  >
    <div class="archive__top">
      <slot name="top" />
    </div>

    <div class="archive__container">
      <TemplateTwoColumn>
        <div class="archive__header">
          <slot name="header" />
        </div>

        <div
          v-if="articles.length"
          class="archive__content"
        >
          <div
            v-if="isPaginationTop"
            class="archive__pagination archive__pagination--top"
          >
            <MainPagination
              :page-current="pageNumber"
              :pages-total="totalPages"
              :link="link"
            />
          </div>

          <div
            v-if="!isLoading"
            class="archive__list"
          >
            <ArticleCardSmallList
              :articles="articles"
              :meta="meta"
              :open-links-in-new-tab="openLinksInNewTab"
              :indexes-for-eager-loading="[0, 1, 2]"
            />
          </div>
          <ArticleCardSmallPlaceholder v-else />

          <div class="archive__pagination archive__pagination--bottom">
            <MainPagination
              :page-current="pageNumber"
              :pages-total="totalPages"
              :link="link"
            />
          </div>
        </div>
        <div
          v-else
          class="archive__no-content"
        >
          <slot name="no-content" />
        </div>
      </TemplateTwoColumn>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import TemplateTwoColumn from '@/components/templates/TwoColumn.vue';
  import type { IMetaArchives } from '~/types/api/archives';
  import type { TArticle } from '~/types/common';
  import ArticleCardSmallList from '~/components/ArticleCardSmallList.vue';
  import MainPagination from '~/components/BaseComponents/MainPagination.vue';
  import ArticleCardSmallPlaceholder from '~/components/ArticleCardSmallPlaceholder/ArticleCardSmallPlaceholder.vue';

  export default defineNuxtComponent({
    name: 'BaseArchive',

    components: {
      TemplateTwoColumn,
      MainPagination,
      ArticleCardSmallList,
      ArticleCardSmallPlaceholder,
    },

    inject: {
      eventActionType: {
        default() {
          return () => '';
        },
      },

      eventCategory: {
        default() {
          return () => '';
        },
      },
    },

    scrollToTop: true,

    props: {
      articles: {
        type: Array as PropType<TArticle[]>,
        required: true,
      },

      meta: {
        type: Object as PropType<IMetaArchives>,
        required: true,
      },

      slug: {
        type: String as PropType<string>,
        default: '',
      },

      // TODO: передавать link в MainPagination
      // через provide/inject
      link: {
        type: Object as PropType<HTMLAnchorElement>,
        required: true,
      },

      openLinksInNewTab: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      isLoading: false,
    }),

    computed: {
      pageNumber(): number {
        return Number(this.meta?.current_page) || 1;
      },

      totalPages(): number {
        return this.meta?.total_pages;
      },

      isPaginationTop(): boolean {
        return this.pageNumber !== 1;
      },
    },

    watch: {
      pageNumber() {
        this.isLoading = false;
      },
    },

    methods: {
      getClassArchive(prefix: string = ''): Record<string, boolean> {
        const {
          $parent: {
            $options: { name: parentName },
          },
        } = this;

        return {
          [`archive${prefix}--${parentName?.toLowerCase()}`]: true,
          [`archive--${this.slug}`]: !!this.slug,
        };
      },
    },
  });
</script>

<style lang="scss" scoped>
  .archive {
    $root: &;

    width: 100%;
    background-color: black;
    padding: 2px 2px 0;

    &__container {
      // * для брендирования, чтобы баннер не перекрывал контент
      position: relative;
      z-index: 2;
    }

    &__top {
      position: relative;
      z-index: 2;
    }

    @include tablet {
      margin-top: 24px;
      padding: $spacing-0;
      background-color: transparent;
    }
    // TODO перенести стиля по архивам/тэгам категорий, в компоненту архива по категориям
    // или сделать стили для отдельного типа архива с обложкой/цветом подложки
    &--archivecategory,
    &--archivetag {
      #{$root} {
        &__top {
          @include desktop {
            background-color: $mainWhite;
            border-radius: 60px 60px 0 0;
            padding: $spacing-9 $spacing-9 $spacing-0;
            max-width: 1280px;
            margin: $spacing-0 auto;
          }
        }

        &__container {
          background: transparent;

          @include tablet {
            padding: $spacing-0;
          }

          @include desktop {
            background-color: $mainWhite;
            border-radius: 0;
            padding-right: $spacing-19;
            padding-left: $spacing-9;
          }
        }

        &__header {
          padding: 0;
          margin: 0;
        }

        &__content {
          background-color: $mainWhite;
          border-radius: 32px;
          padding-bottom: $spacing-4;

          @include tablet {
            border-radius: 0;
            padding: $spacing-0 $spacing-6 $spacing-6;
          }

          @include big-tablet {
            padding: $spacing-0 $spacing-8 $spacing-6;
          }

          @include desktop {
            padding: $spacing-0 $spacing-0 $spacing-6;
          }
        }
      }

      &.archive-category--simple,
      &.archive-tag--simple {
        #{$root} {
          &__content {
            border-radius: 0 0 32px 32px;
          }
        }
      }
    }

    &__container {
      @include container;
      padding-bottom: $spacing-6;
      // height: 100%;
      @include desktop {
        padding-left: $spacing-9;
        padding-right: $spacing-19;
        max-width: 1280px;
      }
    }

    &__header {
      padding: 24px 16px 20px 16px;

      @include tablet {
        padding: 0 24px;
      }

      @include big-tablet {
        padding: 0 32px;
      }

      @include desktop {
        padding: 0 40px;
      }
    }

    &__no-content {
      padding: 16px;

      @include tablet {
        padding: 0 24px;
      }

      @include big-tablet {
        padding: 0 32px;
      }

      @include desktop {
        padding: 0 40px;
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      flex: 1 0 auto;
    }

    &__list {
      flex: 1 0 auto;
    }

    &__pagination {
      padding: $spacing-2 0;

      &--top {
        margin-bottom: $spacing-2;
      }

      &--bottom {
        margin-top: $spacing-2;
      }

      @include tablet {
        padding: $spacing-2 $spacing-6;
      }

      @include big-tablet {
        padding: 8px 32px;
      }

      @include desktop {
        padding: 8px 40px;
      }
    }
  }
</style>
